import type { AppointmentStatus, BookingSource } from "@planerius/contracts"
import type { CabinetAppointmentView } from "@planerius/contracts/cabinet"

import { formatMoney } from "./money"

/**
 * История записи для панели 2-2.3.4 — и честный рассказ о том, чего в ней нет.
 *
 * ЧЕГО НЕ ХВАТАЕТ. Макет показывает четыре строки событий: «создана клиентом через
 * Telegram-бот», «предоплата 500,00 ₽», «подтверждена · Ирина Волкова», «Версия записи».
 * Событийного журнала записи API не отдаёт вовсе: в CabinetAppointmentView есть только
 * source, createdByName, createdAt, отметки отмены и неявки и updatedAt. Право audit.view
 * в контракте есть, а маршрута чтения аудита нет.
 *
 * ЧТО С ЭТИМ СДЕЛАНО. Собирается всё, что о записи действительно известно, и снизу
 * ставится строка о том, что полной истории пока нет. Придумывать недостающие события
 * («подтверждена» без имени и времени) нельзя: панель записи — это то место, куда
 * администратор идёт разбираться в споре с клиентом, и выдуманная строка в ней хуже
 * отсутствующей.
 */

const SOURCE_TEXT: Readonly<Record<BookingSource, string>> = {
  web: "клиентом на странице записи",
  telegram: "клиентом через Telegram-бот",
  max: "клиентом через MAX",
  mobile: "клиентом в приложении",
  widget: "клиентом через виджет на сайте",
  admin: "сотрудником салона",
  api: "через интеграцию",
}

const SELF_BOOKED_CHANNEL: Readonly<Record<BookingSource, string | null>> = {
  web: "записался на странице записи",
  telegram: "записался через Telegram",
  max: "записался через MAX",
  mobile: "записался в приложении",
  widget: "записался через виджет",
  // Салон записал сам — канала нет, и подписи тоже.
  admin: null,
  api: null,
}

/**
 * «записался через Telegram» — КАКИМ каналом клиент записался сам.
 *
 * ЗАЧЕМ КАНАЛ, А НЕ ПРОСТО «ОНЛАЙН». Мастер по этой строке решает, как напомнить
 * о себе: в Telegram он ответит в том же боте, а пришедшему со страницы записи
 * придётся звонить. «Записался онлайн» отвечает на вопрос, которого никто не задавал,
 * — и ровно так и было написано до этой правки.
 *
 * Пусто (null) означает «записал салон»: это не канал клиента, и строки быть не должно.
 */
export function selfBookedChannel(source: BookingSource): string | null {
  return SELF_BOOKED_CHANNEL[source]
}

const CANCEL_ACTOR: Readonly<Record<"client" | "staff" | "system", string>> = {
  client: "клиентом",
  staff: "салоном",
  system: "системой",
}

const MOMENT = new Map<string, Intl.DateTimeFormat>()

/** «10 сент., 21:14» — строка события в истории. */
export function formatMoment(instant: string, timeZone: string): string {
  let format = MOMENT.get(timeZone)
  if (format === undefined) {
    format = new Intl.DateTimeFormat("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    })
    MOMENT.set(timeZone, format)
  }
  return format.format(new Date(instant))
}

/** «11 сент., 09:02:17» — версия записи. Секунды нужны: версия сравнивается с точностью до них. */
export function formatVersion(instant: string, timeZone: string): string {
  return `${formatMoment(instant, timeZone)}:${String(new Date(instant).getUTCSeconds()).padStart(2, "0")}`
}

export function appointmentHistory(
  appointment: CabinetAppointmentView,
  timeZone: string,
): readonly string[] {
  const rows: string[] = []

  const by =
    appointment.source === "admin" && appointment.createdByName !== null
      ? `сотрудником ${appointment.createdByName}`
      : SOURCE_TEXT[appointment.source]
  rows.push(`${formatMoment(appointment.createdAt, timeZone)} — создана ${by}`)

  // Момента предоплаты в ответе нет — только сумма. Поэтому строка без времени
  // и без обещания, что это произошло именно при создании.
  if (appointment.prepaidMinor !== null && appointment.prepaidMinor > 0) {
    rows.push(`Внесена предоплата ${formatMoney(appointment.prepaidMinor)}`)
  }

  if (appointment.noShowAt !== null) {
    rows.push(`${formatMoment(appointment.noShowAt, timeZone)} — отмечена неявка`)
  }

  if (appointment.cancelledAt !== null) {
    const actor = appointment.cancelledBy === null ? "" : ` ${CANCEL_ACTOR[appointment.cancelledBy]}`
    const reason =
      appointment.cancelReasonTitle === null ? "" : ` · ${appointment.cancelReasonTitle}`
    rows.push(`${formatMoment(appointment.cancelledAt, timeZone)} — отменена${actor}${reason}`)
  }

  if (appointment.rescheduledFromId !== null) {
    rows.push("Перенесена с другого времени — это продолжение прежней записи")
  }

  return rows
}

export const HISTORY_GAP_NOTE =
  "Показано то, что известно о записи. Кто и когда подтвердил визит, история пока не хранит: " +
  "журнала событий записи API не отдаёт."

/** Что можно сделать с записью в этом статусе. Чистая функция — её проверяет тест. */
export interface PanelActionSet {
  /** Главное действие в подвале. null — у записи в этом статусе его нет. */
  readonly primary: "arrived" | "completed" | "undo-no-show" | null
  /**
   * «Подтвердить» — только у записи, ждущей подтверждения салоном. Отдельной кнопкой,
   * а не главным действием: клиент мог прийти и без подтверждения, и «Клиент пришёл»
   * остаётся под рукой.
   */
  readonly canConfirm: boolean
  readonly canReschedule: boolean
  readonly canCancel: boolean
  readonly canNoShow: boolean
  readonly canDuplicate: boolean
}

/** Сутки, в течение которых отметку неявки можно снять (ТЗ §2.9.3 и макет). */
export const NO_SHOW_UNDO_WINDOW_MS = 24 * 60 * 60 * 1000

export function canUndoNoShow(noShowAt: string | null, now: number): boolean {
  if (noShowAt === null) return false
  const marked = Date.parse(noShowAt)
  if (Number.isNaN(marked)) return false
  return now - marked <= NO_SHOW_UNDO_WINDOW_MS
}

/**
 * Набор действий по статусу.
 *
 * ПОЧЕМУ ЗАВЕРШЁННЫЙ ВИЗИТ НЕ ОТМЕНЯЕТСЯ. Отмена — это про будущее: она освобождает
 * время и решает судьбу предоплаты. У состоявшегося визита отменять нечего, а ошибку
 * в нём исправляют правкой состава, а не отменой (ТЗ §2.8.3).
 */
export function panelActions(
  status: AppointmentStatus,
  undoAvailable: boolean,
): PanelActionSet {
  switch (status) {
    case "pending":
    case "confirmed":
      return {
        primary: "arrived",
        canConfirm: status === "pending",
        canReschedule: true,
        canCancel: true,
        canNoShow: true,
        canDuplicate: true,
      }
    case "in_progress":
      return {
        primary: "completed",
        canConfirm: false,
        // Идущий визит переносить поздно, а отменять — можно: клиент ушёл посреди визита.
        canReschedule: false,
        canCancel: true,
        canNoShow: false,
        canDuplicate: true,
      }
    case "completed":
      return {
        primary: null,
        canConfirm: false,
        canReschedule: false,
        canCancel: false,
        canNoShow: false,
        canDuplicate: true,
      }
    case "no_show":
      return {
        primary: undoAvailable ? "undo-no-show" : null,
        canConfirm: false,
        canReschedule: false,
        canCancel: false,
        canNoShow: false,
        canDuplicate: true,
      }
    case "cancelled":
      return {
        primary: null,
        canConfirm: false,
        canReschedule: false,
        canCancel: false,
        canNoShow: false,
        canDuplicate: true,
      }
    default:
      return {
        primary: null,
        canConfirm: false,
        canReschedule: false,
        canCancel: false,
        canNoShow: false,
        canDuplicate: false,
      }
  }
}
